<template>
	<section>
		<ButtonBack />

		<h1>Alle Entscheidungen</h1>

		<h2>Filter</h2>
		<TheForm :model="request" :rules="rules" :id="id" ref="formRef" :persistForm="true" formNamespace="faellePublicFilter" :autoSave="true">
			<el-row>
				<el-form-item label="Freitextsuche" prop="searchTerm" class="el-col el-col-24 el-col-sm-12 el-col-xs-24">
					<el-input v-model="request.searchTerm" placeholder="Suche" clearable />
				</el-form-item>

				<el-form-item label="Beschwerdegrund" prop="category" class="el-col el-col-24 el-col-sm-12 el-col-xs-24">
					<el-select v-model="request.category" placeholder="Alle">
						<el-option v-for="item in categoryOptions" :key="item.value" :label="item.label" :value="item" />
					</el-select>
				</el-form-item>
			</el-row>

			<el-row>
				<el-form-item label="Medium" prop="medium" class="el-col el-col-24 el-col-sm-12 el-col-xs-24">
					<el-select v-model="request.medium" placeholder="Alle">
						<el-option v-for="item in mediaOptions" :key="item.value" :label="item.label" :value="item" />
					</el-select>
				</el-form-item>

				<el-form-item label="Branche" prop="sector" class="el-col el-col-24 el-col-sm-12 el-col-xs-24">
					<el-select v-model="request.sector" placeholder="Alle">
						<el-option v-for="item in sectorOptions" :key="item.value" :label="item.label" :value="item" />
					</el-select>
				</el-form-item>
			</el-row>

			<el-row>
				<el-form-item label="Zeitraum (Erstellung)" prop="timePeriod" class="el-col el-col-24 el-col-sm-12 el-col-xs-24">
					<el-checkbox-group v-model="selectedTimePeriod" placeholder="Alle">
						<el-checkbox
							v-for="item in timePeriodOptions"
							:key="item.value"
							:value="item.value"
							:border="true"
							@change="() => handleClickOptionWrapper(item)"
						>
							{{ item.label }}
						</el-checkbox>
					</el-checkbox-group>
				</el-form-item>

				<el-col :sm="4" :xs="24"></el-col>

				<el-form-item label="" prop="dateRange" class="el-col el-col-24 el-col-sm-8 el-col-xs-24">
					<div class="datepicker-range__labels">
						<label class="el-form-item__label">Von</label>
						<label class="el-form-item__label">Bis</label>
					</div>
					<el-date-picker
						v-model="dateRange"
						type="daterange"
						:format="'DD.MM.YYYY'"
						range-separator=""
						:clearable="true"
						start-placeholder="TT.MM.JJJJ"
						end-placeholder="TT.MM.JJJJ"
					/>
				</el-form-item>
			</el-row>

			<el-row class="button-panel">
				<TheButton type="danger" @buttonclick="doSearch()" isSubmit>Anwenden</TheButton>
				<TheButton type="default" @buttonclick="resetSearch">
					<el-icon><Close /></el-icon>
					Filter zurücksetzen
				</TheButton>
			</el-row>
		</TheForm>
	</section>

	<div class="bg--white">
		<section id="searchresults">
			<h2>Entscheidungen ({{ $getCountErgebnisseLabel(casesTotalCount) }})</h2>
			<PublicCasesTable
				:tableData="filteredCases"
				:loading="casesTableLoading"
				:totalCount="casesTotalCount"
				@loadNextPage="loadNextPage"
				:mobileBox="true"
			/>
		</section>
	</div>
</template>

<script lang="ts">
import ButtonBack from '@/components/buttons/ButtonBack.vue';
import TheButton from '@/components/TheButton.vue';
import TheForm from '@/components/TheForm.vue';
import { searchPublicCasesAndComplaints } from '@/requests/searches.js';
import { CaseAndComplaintPublicResponse, SearchCaseRequest } from '@/types/types';
import { returnFormOptions } from '@/utils/formOptions.js';
import { handleClickTimePeriodOptionCheckbox, handleSearchDateRangeChange, prepareSearchRequest } from '@/utils/search/searchUtils';
import { validateForm, resetForm } from '@/utils/functions';
import { Close, Plus } from '@element-plus/icons-vue';
import { FormInstance } from 'element-plus';
import { defineComponent, ref } from 'vue';
import { texts } from '../utils/texts';
import PublicCasesTable from '@/components/tables/PublicCasesTable.vue';

interface SearchCasesData {
	request: SearchCaseRequest;
	rules: Object;
	id: string;
	dateRange: string[] | null;
	selectedTimePeriod: any[];
	tablePage: number;
	itemsPerPage: number;
	filteredCases: CaseAndComplaintPublicResponse[];
	casesTotalCount: number;
	casesTableLoading: boolean;
}

export default defineComponent({
	name: 'FaellePublicView',
	data(): SearchCasesData {
		return {
			request: {
				searchTerm: '',
				category: null,
				medium: null,
				states: null,
				isPublished: null,
				fromDate: null,
				toDate: null,
				reach: null,
				sector: null,
			},
			rules: {
				searchTerm: [{ min: 3, message: texts.formErrors.minChars, trigger: 'blur' }],
			},
			id: 'searchForm',
			dateRange: null,
			selectedTimePeriod: [],
			tablePage: 0,
			itemsPerPage: 14,
			filteredCases: [],
			casesTotalCount: 0,
			casesTableLoading: false,
		};
	},
	setup() {
		const formRef = ref<FormInstance>();
		return {
			formRef,
		};
	},
	components: {
		ButtonBack,
		TheButton,
		TheForm,
		Plus,
		Close,
		PublicCasesTable,
	},
	computed: {
		categoryOptions() {
			return returnFormOptions('complaintAndCaseCategories', false, true);
		},
		mediaOptions() {
			return returnFormOptions('media', false, true);
		},
		sectorOptions() {
			return returnFormOptions('sector', false, true);
		},
		timePeriodOptions() {
			return returnFormOptions('timePeriod');
		},
	},
	methods: {
		handleClickOptionWrapper(item) {
			const { newSelectedTimePeriod, newTimePeriodOptions, newDateRange } = handleClickTimePeriodOptionCheckbox(
				item,
				this.selectedTimePeriod,
				this.timePeriodOptions,
			);

			this.selectedTimePeriod = newSelectedTimePeriod;
			this.timePeriodOptions = newTimePeriodOptions;
			this.dateRange = newDateRange;
		},
		resetSearch() {
			resetForm(this.formRef, this.id);
			this.dateRange = null;
			this.doSearch();
		},
		async doSearch() {
			const isValid = await validateForm(this.formRef, this.id);
			if (!isValid) return;

			this.casesTableLoading = true;
			let searchRequest = prepareSearchRequest(this.request) as SearchCaseRequest;
			if (searchRequest.states) {
				//@ts-ignore
				searchRequest.states = [searchRequest.states];
			}

			const searchResult = await searchPublicCasesAndComplaints(searchRequest, this.tablePage, this.itemsPerPage);
			if (searchResult.success) {
				this.casesTotalCount = searchResult.data.totalCount;
				if (this.tablePage === 0) {
					this.filteredCases = searchResult.data.items;
				} else {
					this.filteredCases = [...this.filteredCases, ...searchResult.data.items];
				}
			} else {
				this.filteredCases = [];
				this.casesTotalCount = 0;
			}
			this.casesTableLoading = false;
		},
		loadNextPage() {
			this.tablePage++;
			this.doSearch();
		},
	},
	mounted() {
		if (this.$route.query.searchTerm) {
			this.request.searchTerm = String(this.$route.query.searchTerm);
		}
		this.doSearch();
	},
	watch: {
		dateRange: {
			handler(val) {
				if (val && val.length === 2) {
					const { fromDate, toDate } = handleSearchDateRangeChange(val);
					this.request.fromDate = fromDate;
					this.request.toDate = toDate;
				} else {
					this.request.fromDate = null;
					this.request.toDate = null;
					this.selectedTimePeriod = [];
					this.timePeriodOptions.forEach((option: any) => (option.checked = false));
				}
			},
			deep: true,
		},
		request: {
			handler() {
				if (this.request.fromDate && this.request.toDate) {
					this.dateRange = [this.request.fromDate, this.request.toDate];
				}
			},
			deep: true,
		},
	},
});
</script>
